import { lazy, Suspense, useEffect } from "react";
import { savePendingDeepLink } from "@/lib/notifications/pendingDeepLink";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/components/ThemeProvider";
import ScrollToTop from "./components/ScrollToTop";
import { AnalyticsTracker } from "./components/AnalyticsTracker";
import RouteFallback from "./components/RouteFallback";
import LazyChunkErrorBoundary from "./components/LazyChunkErrorBoundary";
import SWUpdatePrompt from "./components/SWUpdatePrompt";
import { UploadProvider } from "./components/uploads/UploadProvider";
import { ScopedUploadTray } from "./components/uploads/ScopedUploadTray";
import { TrialStatusBanner } from "./components/billing/TrialStatusBanner";

const FinanceLogin = lazy(() => import("./pages/FinanceLogin"));
const FinanceDashboard = lazy(() => import("./pages/FinanceDashboard"));
const Handoff = lazy(() => import("./pages/auth/Handoff"));
const Billing = lazy(() => import("./pages/billing/Billing"));
const PublicInvoicePay = lazy(() => import("./pages/PublicInvoicePay"));
const NotFound = lazy(() => import("./pages/NotFound"));
const FintechTemplatePreview = lazy(
  () => import("./components/ui/__previews__/FintechTemplate"),
);

const queryClient = new QueryClient();

const FinanceApp = () => {
  // Capture deep links on first mount so the login page can resume them.
  useEffect(() => {
    try {
      const path = window.location.pathname;
      const search = window.location.search;
      const full = path + search;
      if (
        path !== "/login" &&
        path !== "/" &&
        path !== "/auth/handoff" &&
        !path.startsWith("/pay/")
      ) {
        savePendingDeepLink("finance", full);
      }
    } catch {
      /* noop */
    }
  }, []);

  return (
    <>
      <AnalyticsTracker />
      <ScrollToTop />
      <div className="min-h-screen bg-background text-foreground flex flex-col">
        <TrialStatusBanner />
        <main className="flex-1">
          <LazyChunkErrorBoundary>
            <Routes>
              <Route path="/login" element={<Suspense fallback={<RouteFallback variant="auth" />}><FinanceLogin /></Suspense>} />
              <Route path="/auth/handoff" element={<Suspense fallback={<RouteFallback variant="auth" />}><Handoff /></Suspense>} />
              <Route path="/billing" element={<Suspense fallback={<RouteFallback variant="billing" />}><Billing /></Suspense>} />
              <Route path="/pay/:invoiceId" element={<Suspense fallback={<RouteFallback variant="pay" />}><PublicInvoicePay /></Suspense>} />
              {/* Dev-only preview of the canonical Fintech Template primitives. */}
              <Route path="/_design" element={<Suspense fallback={<RouteFallback variant="default" />}><FintechTemplatePreview /></Suspense>} />
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard/*" element={<Suspense fallback={<RouteFallback variant="dashboard" />}><FinanceDashboard /></Suspense>} />
              {/* Legacy /finance/* paths -> strip prefix */}
              <Route path="/finance" element={<Navigate to="/dashboard" replace />} />
              <Route path="/finance/login" element={<Navigate to="/login" replace />} />
              <Route path="/finance/billing" element={<Navigate to="/billing" replace />} />
              <Route path="/finance/dashboard/*" element={<Suspense fallback={<RouteFallback variant="dashboard" />}><FinanceDashboard /></Suspense>} />
              <Route path="*" element={<Suspense fallback={<RouteFallback variant="default" />}><NotFound /></Suspense>} />
            </Routes>
          </LazyChunkErrorBoundary>
        </main>
      </div>
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider defaultTheme="light">
      <TooltipProvider delayDuration={200}>
        <Toaster />
        <Sonner />
        <SWUpdatePrompt />
        <BrowserRouter>
          <UploadProvider>
            <FinanceApp />
            <ScopedUploadTray />
          </UploadProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;